import {IAnalyticsMeta} from 'core/types';
import {ICartProduct} from 'modules/cart/models';
import {useDeleteFromCartMutation, useUpdateCartMutation} from 'modules/cart/services';
import {useCallback, useMemo} from 'react';
import {THandleCartCountClickParams} from '../types';
import {CART_MAX_SIZE, COUNT_ACTIONS, TOAST_NOTIFICATIONS} from '../constants';
import {toast} from 'react-toastify';
import {Toast} from 'components/Toast';
import {actionSetAddedProduct} from 'modules/cart/slice';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {useCartEventAnalytics} from 'modules/products/hooks/gtag/useCartEventAnalytics';
import {IProductShort} from 'modules/products/types';

interface IProps {
    id: number;
    cartProductsList?: ICartProduct[];
    product?: Omit<IProductShort, 'stockAmount'>;
    analytics?: IAnalyticsMeta;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const useAddToCartHandler = ({id, cartProductsList = [], product, analytics}: IProps) => {
    const [updateCart, {isLoading: isUpdateLoading}] = useUpdateCartMutation();
    const [deleteProduct, {isLoading: isDeleteLoading}] = useDeleteFromCartMutation();
    const dispatch = useAppDispatch();

    const isCartFull = (cartProductsList.length ?? 0) >= CART_MAX_SIZE;
    const isNewProduct = !cartProductsList.some(({cartData}) => cartData.id === id);

    const cartEventGtag = useCartEventAnalytics();

    const createCartHandler = useCallback(
        ({action, offerId}: THandleCartCountClickParams) => {
            const showCartFullToast = () => {
                toast(<Toast isNeedCloseButton={true} text={TOAST_NOTIFICATIONS.CART_FILLED} theme={'error'} />, {
                    className: 'notification',
                });
            };

            const handleAdd = async (productInCart: ICartProduct | undefined) => {
                const quantity =
                    productInCart && productInCart.cartData?.quantity >= CART_MAX_SIZE
                        ? CART_MAX_SIZE
                        : (productInCart?.cartData?.quantity || 0) + 1;

                const response = await updateCart({id: productInCart?.cartData?.id, quantity});
                if ('error' in response) {
                    return;
                }

                cartEventGtag(product as any, quantity, analytics);

                const productList = response.data.products;

                const addedProduct = productList.find(({offerId: cartProductId}) => cartProductId === id);
                if (addedProduct) {
                    dispatch(actionSetAddedProduct(addedProduct));
                }
            };

            const handleRemove = async (productInCart: ICartProduct) => {
                await deleteProduct({id: productInCart.cartData?.id}).then(() =>
                    cartEventGtag(product as any, 0, analytics),
                );
            };

            const handleDecrease = async (productInCart: ICartProduct) => {
                await updateCart({
                    id: productInCart.cartData?.id,
                    quantity: productInCart.cartData?.quantity - 1,
                }).then(() => cartEventGtag(product as any, productInCart.cartData?.quantity - 1, analytics));
            };

            // eslint-disable-next-line complexity
            return async () => {
                if (isUpdateLoading || isDeleteLoading) {
                    return;
                }
                const productInCart = cartProductsList.find((item) => item.offerId === Number(offerId));
                const isProductNeedDelete = productInCart && 0 >= productInCart?.cartData?.quantity - 1;

                if (isCartFull && isNewProduct) {
                    showCartFullToast();
                    return;
                }

                if (action === COUNT_ACTIONS.ADD || (offerId && !productInCart?.cartData?.quantity)) {
                    await handleAdd(productInCart);
                    return;
                }

                if (productInCart) {
                    if (isProductNeedDelete && action === COUNT_ACTIONS.REMOVE) {
                        await handleRemove(productInCart);
                        return;
                    }
                    await handleDecrease(productInCart);
                }
            };
        },
        [
            cartEventGtag,
            product,
            analytics,
            updateCart,
            id,
            dispatch,
            deleteProduct,
            isUpdateLoading,
            isDeleteLoading,
            cartProductsList,
            isCartFull,
            isNewProduct,
        ],
    );

    const isLoading = isDeleteLoading || isUpdateLoading;

    return useMemo(
        () => ({
            createCartHandler,
            isLoading,
        }),
        [createCartHandler, isLoading],
    );
};
